import Decimal from "decimal.js";
import { serializeMoney, tryParseMoney, type MoneyInput } from "./money";

/**
 * One invoice line used to derive the payable total. `totalPrice` is the
 * extended amount (quantity × unit price), before header tax.
 */
export interface InvoiceTotalLine {
  lineNumber?: number | null;
  quantity?: MoneyInput | null;
  unitPrice?: MoneyInput | null;
  totalPrice?: MoneyInput | null;
}

export interface InvoiceTotalSnapshot {
  taxAmount?: MoneyInput | null;
  lineItems?: readonly InvoiceTotalLine[] | null;
}

export interface InvoiceTotals {
  subtotal: string;
  totalAmount: string;
}

/**
 * Why the line-item total is not the total read from the document.
 * `extractionDiffers` means the extracted total does not match the extracted
 * lines. `invoiceUpdated` means the saved lines or tax are not what was extracted.
 */
export interface ExtractedTotalWarning {
  currentTotal: string;
  extractedTotal: string;
  extractedLineSum: string | null;
  extractionDiffers: boolean;
  invoiceUpdated: boolean;
}

function isBlank(value: MoneyInput | null | undefined): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

function readMoney(value: MoneyInput | null | undefined): Decimal | null {
  if (isBlank(value)) return null;
  return tryParseMoney(value);
}

/** Blank tax is zero. An unreadable tax cannot be added. */
function readTax(value: MoneyInput | null | undefined): Decimal | null {
  if (isBlank(value)) return new Decimal(0);
  return tryParseMoney(value);
}

function canonMoney(value: MoneyInput | null | undefined): string | null {
  const parsed = readTax(value);
  if (!parsed) return null;
  return serializeMoney(parsed);
}

function canonDecimal(
  value: MoneyInput | null | undefined,
  places: number,
): string | null {
  if (isBlank(value)) return "";
  const parsed = tryParseMoney(value);
  if (!parsed) return null;
  const fixed = parsed
    .toDecimalPlaces(places, Decimal.ROUND_HALF_EVEN)
    .toFixed(places);
  return fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

/**
 * Payable total for an invoice: the sum of line extensions, plus header tax.
 * Header tax is the invoice's tax figure, so per-line tax is not added again.
 * Returns null when there are no lines or an amount cannot be read — the
 * stored total is left alone in that case.
 */
export function invoiceTotalFromLines(
  input: InvoiceTotalSnapshot,
): InvoiceTotals | null {
  const lines = input.lineItems ?? [];
  if (lines.length === 0) return null;

  let subtotal = new Decimal(0);
  for (const line of lines) {
    const price = readMoney(line.totalPrice);
    if (!price) return null;
    subtotal = subtotal.plus(price);
  }

  const tax = readTax(input.taxAmount);
  if (!tax) return null;

  return {
    subtotal: serializeMoney(subtotal),
    totalAmount: serializeMoney(subtotal.plus(tax)),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asMoney(value: unknown): MoneyInput | null {
  if (typeof value === "string" || typeof value === "number") return value;
  if (value instanceof Decimal) return value;
  return null;
}

function readExtracted(extracted: unknown): InvoiceTotalSnapshot | null {
  const record = asRecord(extracted);
  if (!record) return null;
  const lineItems = Array.isArray(record.lineItems)
    ? record.lineItems.flatMap((line) => {
        const row = asRecord(line);
        if (!row) return [];
        return [
          {
            lineNumber:
              typeof row.lineNumber === "number" ? row.lineNumber : null,
            quantity: asMoney(row.quantity),
            unitPrice: asMoney(row.unitPrice),
            totalPrice: asMoney(row.totalPrice),
          },
        ];
      })
    : null;
  return {
    taxAmount: asMoney(record.taxAmount),
    lineItems,
  };
}

function lineSignature(line: InvoiceTotalLine): string | null {
  const quantity = canonDecimal(line.quantity, 4);
  const unitPrice = canonDecimal(line.unitPrice, 4);
  const totalPrice = canonMoney(line.totalPrice);
  if (quantity == null || unitPrice == null || totalPrice == null) return null;
  const lineNumber =
    line.lineNumber != null && Number.isInteger(line.lineNumber)
      ? String(line.lineNumber)
      : "";
  return `${lineNumber}|${quantity}|${unitPrice}|${totalPrice}`;
}

function signature(snapshot: InvoiceTotalSnapshot): string | null {
  const tax = canonMoney(snapshot.taxAmount);
  if (tax == null || snapshot.lineItems == null) return null;
  const lines: string[] = [];
  for (const line of snapshot.lineItems) {
    const signed = lineSignature(line);
    if (signed == null) return null;
    lines.push(signed);
  }
  lines.sort();
  return `${tax}#${lines.join(";")}`;
}

/**
 * Non-blocking note when the line-item total is not the total extracted from
 * the document. Approval does not use this. No extraction, or totals that
 * agree, returns null.
 */
export function explainExtractedTotalGap(
  current: InvoiceTotalSnapshot,
  extracted: unknown,
): ExtractedTotalWarning | null {
  const currentTotals = invoiceTotalFromLines(current);
  const extractedRecord = asRecord(extracted);
  if (!currentTotals || !extractedRecord) return null;

  const extractedTotalValue = readMoney(asMoney(extractedRecord.totalAmount));
  if (!extractedTotalValue) return null;
  const extractedTotal = serializeMoney(extractedTotalValue);
  if (currentTotals.totalAmount === extractedTotal) return null;

  const extractedSnapshot = readExtracted(extracted);
  const extractedTotals = extractedSnapshot
    ? invoiceTotalFromLines(extractedSnapshot)
    : null;
  const currentSignature = signature({
    taxAmount: current.taxAmount,
    lineItems: current.lineItems ?? [],
  });
  const extractedSignature = extractedSnapshot
    ? signature(extractedSnapshot)
    : null;

  return {
    currentTotal: currentTotals.totalAmount,
    extractedTotal,
    extractedLineSum: extractedTotals?.totalAmount ?? null,
    extractionDiffers:
      extractedTotals != null && extractedTotals.totalAmount !== extractedTotal,
    invoiceUpdated:
      currentSignature != null &&
      extractedSignature != null &&
      currentSignature !== extractedSignature,
  };
}
